<template>
  <div class="p-field">
    <div class="p-field-horizontal">
      <label v-if="label" :for="id" class="label-text">
        {{ label }}
        <span v-if="required" class="text-red-500">*</span>
      </label>
      <div class="p-slider-container">
        <Slider
          ref="inputRef"
          :id="id"
          v-model="localValue"
          :min="min"
          :max="max"
          :step="step"
          :range="range"
          :disabled="disabled"
          :required="required"
          :orientation="orientation"
          :class="[
            'one-slider',
            {
              'p-invalid': isInvalid,
              'p-disabled': disabled
            }
          ]"
          :aria-label="props['aria-label']"
          :aria-labelledby="props['aria-labelledby']"
          :aria-invalid="isInvalid"
          :aria-required="required"
          :aria-describedby="ariaDescribedBy"
          :pt="{
            root: { class: wrapperClass }
          }"
          @update:modelValue="onValueUpdate"
        />
        <div v-if="showValue" class="p-slider-value">
          {{ Array.isArray(localValue) ? `${localValue[0]} - ${localValue[1]}` : localValue }}
          <span v-if="unit">{{ unit }}</span>
        </div>
      </div>
    </div>

    <!-- Error Messages -->
    <div v-if="showErrorMessages" class="p-error-messages">
      <small v-if="helperText" :id="helperTextId" class="p-error block">{{ helperText }}</small>
      <small
        v-for="(error, index) in localizedErrors"
        :key="index"
        :id="`${id}-error-${index}`"
        class="p-error block"
      >
        {{ error }}
      </small>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import Slider from 'primevue/slider'

export default defineComponent({
  name: 'ONESlider',
  components: {
    Slider
  },
  inheritAttrs: false
})
</script>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useFormContext } from '../../../../composables/useFormContext'
import { useLocalization } from '../../../../composables/useLocalization'
import type { ONESliderProps } from './types'

const props = withDefaults(defineProps<ONESliderProps>(), {
  modelValue: undefined,
  min: 0,
  max: 100,
  step: 1,
  range: false,
  disabled: false,
  required: false,
  orientation: 'horizontal',
  showValue: true,
  unit: undefined,
  label: undefined,
  helperText: '',
  invalid: false,
  id: () => `one-slider-${Math.random().toString(36).substr(2, 9)}`,
  'aria-label': '',
  'aria-labelledby': '',
  validationRules: () => [],
  validateOnBlur: true,
  validateOnInput: false,
  validateOnChange: true,
  showErrors: true,
  name: undefined,
  formContext: true,
  wrapperClass: undefined
})

const emit = defineEmits([
  'update:modelValue',
  'change',
  'validation-error',
  'validation-success'
])

// Form Context
const { formState } = useFormContext()
const { t } = useLocalization()

// Local State
const inputRef = ref<any>(null)
const localValidationErrors = ref<string[]>([])
const isDirty = ref(false)
const isValidating = ref(false)

const localValue = computed({
  get: () => props.modelValue,
  set: (newValue) => {
    emit('update:modelValue', newValue)
    if (formState && props.name) {
      formState.setFieldValue(props.name, newValue)
    }
  }
})

const currentErrors = computed(() => {
  if (formState && props.name) {
    return formState.errors.value[props.name] || []
  }
  return localValidationErrors.value
})

const localizedErrors = computed(() => {
  return currentErrors.value.map(error => {
    if (error.startsWith('validation.')) {
      return t(error)
    }
    if (error.toLowerCase().includes('required')) {
      return t('validation.required')
    }
    return error
  })
})

const isInvalid = computed(() => {
  return props.invalid || (currentErrors.value.length > 0 && isDirty.value)
})

const helperTextId = computed(() => `${props.id}-helper`)

const ariaDescribedBy = computed(() => {
  const ids = []
  if (props.helperText) ids.push(helperTextId.value)
  currentErrors.value.forEach((_, index) => ids.push(`${props.id}-error-${index}`))
  return ids.join(' ')
})

const showErrorMessages = computed(() => {
  return props.showErrors && (!!props.helperText || (currentErrors.value.length > 0 && isDirty.value))
})

const validate = async (): Promise<boolean> => {
  if (formState && props.name) {
    return formState.validateField(props.name)
  }

  isValidating.value = true
  try {
    const errors: string[] = []
    const value = localValue.value

    for (const rule of props.validationRules) {
      let isValid = true
      const message = rule.message

      if (rule.validator) {
        isValid = await rule.validator(value)
      }

      if (!isValid && message) {
        errors.push(message)
      }
    }

    localValidationErrors.value = errors
    if (errors.length > 0) {
      emit('validation-error', errors)
      return false
    }

    emit('validation-success')
    return true
  } finally {
    isValidating.value = false
  }
}

const onValueUpdate = async (value: number | number[]) => {
  emit('change', value)
  isDirty.value = true
  if (props.validateOnChange) {
    await validate()
  }
}

// Initialize form context
onMounted(() => {
  if (formState && props.name && props.formContext && props.validationRules.length > 0) {
    formState.addValidation(props.name, props.validationRules)
  }
})

// Watch for external validation triggers
watch(() => props.invalid, (newValue) => {
  if (newValue) {
    isDirty.value = true
  }
})

defineExpose({
  validate,
  validationErrors: currentErrors,
  isDirty,
  isValidating,
  isValid: computed(() => currentErrors.value.length === 0)
})
</script>

